/**
 * 거래처 자동완성 — 미수금·수입지출·결재 폼에서 사용.
 *
 * <p>UX 정책:
 *   - 입력 비어있어도 dropdown 열면 "전체 조회" → 등록된 거래처 첫 page 노출
 *   - 입력하면 suggest 호출 (디바운스 — AppAutocomplete 기본값)
 *   - {@code freeSolo=true} 면 매칭 옵션이 없을 때 "[입력값] 그대로 입력하기" 옵션 자동 추가
 *
 * <p>props:
 *   - value (number|null)            — 현재 선택된 거래처 id
 *   - initialName (string|null)      — 수정 모드에서 처음 표시할 이름
 *   - onChange(idOrNull, vendorOrString)
 *      • 옵션 선택  → onChange(vendor.id, vendorObj)
 *      • 자유 입력  → onChange(null, "친 글자")          [freeSolo 모드만]
 *      • 비움      → onChange(null, null)
 *   - label (string|null)            — null 이면 외부 FormField 사용 (라벨 중복 방지)
 *   - freeSolo (boolean)             — 등록 안 된 거래처도 그대로 입력 허용
 *
 * <p>내부적으로 {@link AppAutocomplete} 위에 거래처 도메인 표시 규칙(타입 칩, 사업자번호) 만
 * 얹은 얇은 wrapper. 로딩 표기·드롭다운 위치·디바운스 등 공통 동작은 모두 AppAutocomplete 위임.
 */
import React, { useEffect, useState } from "react";
import { Box, Chip, Typography } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import AppAutocomplete from "../../../../component/common/AppAutocomplete";
import { financeApi } from "../../api/financeApi";

const TYPE_LABEL = { GENERAL: "사업자", PUBLIC: "공공기관" };
const TYPE_BG    = { GENERAL: "rgba(37,99,235,0.10)", PUBLIC: "rgba(22,163,74,0.10)" };
const TYPE_COLOR = { GENERAL: "#1d4ed8", PUBLIC: "#15803d" };

export default function VendorAutocomplete({
    value, initialName,
    onChange,
    disabled = false,
    label,
    placeholder = "거래처명 또는 사업자번호 입력",
    helperText,
    size = "small",
    freeSolo = false,
}) {
    const [input, setInput]       = useState(initialName || "");
    const [selected, setSelected] = useState(
        value ? { id: value, name: initialName || "" } : null
    );

    /* 외부 value/initialName 동기화 */
    useEffect(() => {
        if (value == null) {
            setSelected(null);
            setInput(initialName || "");
        } else if (!selected || selected.id !== value) {
            setSelected({ id: value, name: initialName || "" });
            setInput(initialName || "");
        }
        // eslint-disable-next-line
    }, [value, initialName]);

    const handleSelect = (v) => {
        if (typeof v === "string") {
            // freeSolo + Enter 로 string 으로 들어오는 케이스
            setSelected(null);
            onChange?.(null, v);
            return;
        }
        if (v?.__freeSolo) {
            // "[입력값] 그대로 입력하기" 옵션 클릭
            setSelected(null);
            onChange?.(null, v.name);
            return;
        }
        setSelected(v);
        onChange?.(v?.id ?? null, v ?? null);
    };

    return (
        <AppAutocomplete
            size={size}
            freeSolo={freeSolo}
            disabled={disabled}
            label={label || undefined}
            placeholder={placeholder}
            helperText={helperText}
            value={selected}
            inputValue={input}
            onInputChange={(v) => {
                setInput(v);
                if (freeSolo) {
                    // 자유 입력 — 한 글자 칠 때마다 즉시 onChange 로 텍스트 보냄.
                    // 옵션 선택이 따로 일어나면 onChange 에서 덮어씀.
                    if (selected && selected.name !== v) setSelected(null);
                    onChange?.(null, v);
                }
            }}
            onChange={(v) => handleSelect(v)}
            fetcher={async (q) => {
                let opts;
                if (!q) {
                    const res = await financeApi.listVendors({ q: "", page: 0, size: 20 });
                    const data = res?.data ?? res;
                    opts = data?.content || data?.data?.content || [];
                } else {
                    const res = await financeApi.suggestVendors({ q, size: 10 });
                    opts = Array.isArray(res) ? res : (res?.data ?? []);
                }
                if (freeSolo && q && !opts.some(o => o.name === q)) {
                    return [...opts, { id: -1, name: q, __freeSolo: true }];
                }
                return opts;
            }}
            getOptionLabel={(o) => (typeof o === "string" ? o : (o?.name || ""))}
            isOptionEqualToValue={(o, v) => o?.id === v?.id}
            noOptionsText={input.trim() ? "검색 결과가 없습니다" : "등록된 거래처가 없습니다"}
            renderOption={(props, opt) => {
                const { key, ...rest } = props;
                if (opt.__freeSolo) {
                    return (
                        <Box component="li" key={`__free_${opt.name}`} {...rest} sx={{ py: 0.8, px: 1.2 }}>
                            <AddRoundedIcon sx={{ fontSize: 16, color: "#64748b", mr: 0.8 }} />
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>
                                "{opt.name}"&nbsp;
                                <Box component="span" sx={{ fontWeight: 500, color: "#64748b" }}>
                                    그대로 입력하기
                                </Box>
                            </Typography>
                        </Box>
                    );
                }
                return (
                    <Box component="li" key={opt.id} {...rest} sx={{ py: 0.6, px: 1.2 }}>
                        <Chip size="small" label={TYPE_LABEL[opt.type] || opt.type}
                            sx={{
                                height: 18, fontSize: 10, fontWeight: 700, mr: 1,
                                bgcolor: TYPE_BG[opt.type] || TYPE_BG.GENERAL,
                                color:   TYPE_COLOR[opt.type] || TYPE_COLOR.GENERAL,
                            }} />
                        <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography sx={{
                                fontSize: 13, fontWeight: 700, color: "#0f172a",
                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}>
                                {opt.name}
                            </Typography>
                            {opt.bizRegistrationNo && (
                                <Typography sx={{
                                    fontSize: 11, color: "#94a3b8",
                                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                }}>
                                    {opt.bizRegistrationNo}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                );
            }}
        />
    );
}
